import { useMemo, useState } from 'react'
import { Shield } from 'lucide-react'
import { format, parseISO } from 'date-fns'

import { PageShell } from '@/components/common/PageShell'
import { EmptyState } from '@/components/common/EmptyState'
import { CompanyChips } from '@/features/shifts/components/CompanyChips'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import {
  DataTable,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableCard,
} from '@/components/common/DataTable'
import { Badge } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { cn } from '@/lib/cn'
import { useMyCompanyFilter } from '@/features/companies/hooks/useMyCompanyFilter'
import { useMySecurityLedger } from '../hooks/useMySecurityLedger'
import {
  TYPE_LABEL,
  STATUS_LABEL,
  STATUS_VARIANT,
  DEFAULT_CURRENCY,
  formatLedgerNumber,
  signedLedgerNumber,
  raisesBalance,
} from '../lib/format'
import { QueryErrorState } from '@/components/common/QueryErrorState'

const LIMIT = 10

export function MySecurityDepositsPage(): React.ReactElement {
  const [page, setPage] = useState(1)
  const companyFilter = useMyCompanyFilter({ onChange: () => setPage(1) })
  const { companies: myCompanies, selectedCompanyIds, companyIds, companyNameById } = companyFilter

  const params = useMemo(
    () => ({
      ...(companyIds ? { companyIds } : {}),
      page,
      limit: LIMIT,
    }),
    [companyIds, page]
  )

  const { data, isLoading, isError, error, refetch } = useMySecurityLedger(params)
  const records = data?.data ?? []
  const total = data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <PageShell
      breadcrumbItems={[{ label: 'My Workspace', to: '/dashboard' }, { label: 'My Security Deposit' }]}
      icon={Shield}
      title="My Security Deposit"
      description="Deductions, refunds, and adjustments across every company you belong to."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            <CompanyChips
              companies={myCompanies}
              selectedCompanyIds={selectedCompanyIds}
              onToggle={companyFilter.toggle}
            />
          }
        />

        {isLoading ? (
          <DataTableCard>
            <SectionLoader variant="table" cols={6} rows={6} label="Loading security deposits" />
          </DataTableCard>
        ) : isError ? (
          <DataTableCard>
            <QueryErrorState
              error={error}
              title="Couldn't load your security ledger"
              onRetry={refetch}
              className="border-0 bg-transparent dark:border-0 dark:bg-transparent"
            />
          </DataTableCard>
        ) : records.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="No security deposit activity"
            description="Deductions and refunds against your security deposit will appear here."
          />
        ) : (
          <DataTable>
            <DataTableHead>
              <tr>
                <DataTableHeadCell>Date</DataTableHeadCell>
                <DataTableHeadCell>Company</DataTableHeadCell>
                <DataTableHeadCell>Type</DataTableHeadCell>
                <DataTableHeadCell align="right">Amount</DataTableHeadCell>
                <DataTableHeadCell align="right">Balance after</DataTableHeadCell>
                <DataTableHeadCell>Status</DataTableHeadCell>
              </tr>
            </DataTableHead>
            <DataTableBody>
              {records.map((tx) => {
                const cName = companyNameById.get(tx.companyId) ?? '—'
                const raises = raisesBalance(tx.type)
                return (
                  <DataTableRow key={tx._id}>
                    <DataTableCell>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {format(parseISO(tx.createdAt), 'd MMM yyyy')}
                      </span>
                    </DataTableCell>
                    <DataTableCell>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{cName}</span>
                    </DataTableCell>
                    <DataTableCell>{TYPE_LABEL[tx.type]}</DataTableCell>
                    <DataTableCell align="right">
                      <span
                        className={cn(
                          'font-semibold',
                          raises
                            ? 'text-emerald-700 dark:text-emerald-300'
                            : 'text-red-700 dark:text-red-300'
                        )}
                      >
                        <span className="mr-1 text-xs font-normal text-gray-400">
                          {tx.currency ?? DEFAULT_CURRENCY}
                        </span>
                        {signedLedgerNumber(tx.type, tx.amount)}
                      </span>
                    </DataTableCell>
                    <DataTableCell align="right">
                      <span className="text-gray-700 dark:text-gray-300">
                        {typeof tx.balanceAfter === 'number' ? formatLedgerNumber(tx.balanceAfter) : '—'}
                      </span>
                    </DataTableCell>
                    <DataTableCell>
                      <Badge variant={STATUS_VARIANT[tx.status]}>{STATUS_LABEL[tx.status]}</Badge>
                    </DataTableCell>
                  </DataTableRow>
                )
              })}
            </DataTableBody>
          </DataTable>
        )}

        <TablePaginationBar
          page={page}
          limit={LIMIT}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </PageShell>
  )
}

MySecurityDepositsPage.displayName = 'MySecurityDepositsPage'
